import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { formatUSD } from '../utils/format';
import { countryName } from '../utils/countries';
import { Copy, Check, Mail, ExternalLink } from 'lucide-react';
import './TransferDetails.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PayPalIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" fill="currentColor">
    <path d="M7.3 21.4h-3a.4.4 0 0 1-.4-.5l2.6-16.3a.5.5 0 0 1 .5-.5h6.5c2.5 0 4.5.6 5.3 2 .5 1 .6 2 .3 3.4-.7 3.4-3 4.6-6.4 4.6h-2c-.5 0-.9.3-1 .8l-.6 3.5-.3 1.6c0 .2-.2.4-.4.4Zm5.4-15h-.7l-1 6h.6c2 0 3.7-.4 4.1-2.5.2-.8 0-1.5-.4-2-.5-.8-1.4-1.5-2.6-1.5Z"/>
  </svg>
);

const frameLabel = (frame, lang) => {
  if (!frame) return '';
  const map = lang === 'es'
    ? { black: 'Negro', silver: 'Plateado', gold: 'Dorado' }
    : { black: 'Black', silver: 'Silver', gold: 'Gold' };
  return map[frame] || frame;
};

const OrderDetails = () => {
  const { reference } = useParams();
  const { lang } = useLanguage();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    if (!reference) return;
    fetch(`${BACKEND_URL}/api/checkout/order/${encodeURIComponent(reference)}`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || 'Order not found');
        }
        return res.json();
      })
      .then(setOrder)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [reference]);

  const copy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch (_e) {}
  };

  const TXT = lang === 'es' ? {
    title: 'Pedido recibido',
    reference: 'Referencia',
    subtotal: 'Subtotal',
    shipping: 'Envío',
    free: 'Gratis',
    total: 'Total a pagar',
    totalPartial: 'Total (sin envío)',
    quoting: 'Por cotizar',
    quoteTitle: 'Tu envío internacional se está cotizando',
    quoteBody: 'Recibimos tu pedido. Estamos preparando la cotización personalizada del envío internacional. Te enviaremos el monto final y el enlace de pago a tu correo en menos de 24 hrs.',
    summary: 'Resumen',
    pending: 'Pendiente de pago',
    paid: 'Pagado',
    back: 'Volver a la galería',
    contact: '¿Necesitas ayuda? Escríbenos',
    contactEmail: 'carlotatorresll@gmail.com',
    items: 'Piezas',
    address: 'Dirección de envío',
    paypalTitle: 'Paga con PayPal',
    paypalDesc: 'Te abriremos PayPal en una nueva pestaña con el monto ya cargado. Por favor incluye la referencia abajo en el campo de notas.',
    paypalCta: 'Pagar',
    bank: 'Datos para tu transferencia',
    bankSoon: 'Los datos bancarios se publicarán muy pronto. Contáctanos por correo para recibirlos.',
    bankName: 'Banco',
    holder: 'Titular',
    account: 'Cuenta',
    clabe: 'CLABE',
    swift: 'SWIFT / BIC',
    referenceNote: 'Nota de referencia',
    instructionsTransfer: 'Una vez recibida la transferencia confirmaremos tu pedido por correo electrónico. Incluye la referencia en el concepto del depósito.',
    instructionsPaypal: 'Una vez recibido tu pago vía PayPal te confirmaremos por correo electrónico.',
  } : {
    title: 'Order received',
    reference: 'Reference',
    subtotal: 'Subtotal',
    shipping: 'Shipping',
    free: 'Free',
    total: 'Total due',
    totalPartial: 'Total (without shipping)',
    quoting: 'Quoting',
    quoteTitle: 'Your international shipping is being quoted',
    quoteBody: 'We received your order. We are preparing a personal international shipping quote. You will receive the final amount and a payment link by email within 24 hrs.',
    summary: 'Summary',
    pending: 'Awaiting payment',
    paid: 'Paid',
    back: 'Back to gallery',
    contact: 'Need help? Email us',
    contactEmail: 'carlotatorresll@gmail.com',
    items: 'Items',
    address: 'Shipping address',
    paypalTitle: 'Pay with PayPal',
    paypalDesc: 'We will open PayPal in a new tab with the amount prefilled. Please include the reference below in the notes field.',
    paypalCta: 'Pay',
    bank: 'Bank transfer details',
    bankSoon: 'Bank details will be published shortly. Please email us to receive them.',
    bankName: 'Bank',
    holder: 'Account holder',
    account: 'Account',
    clabe: 'CLABE',
    swift: 'SWIFT / BIC',
    referenceNote: 'Reference note',
    instructionsTransfer: 'Once we receive your transfer we will confirm by email. Include the reference in the deposit concept.',
    instructionsPaypal: 'Once we receive your payment we will confirm by email.',
  };

  if (loading) return <div className="transfer-page transfer-loading">Loading…</div>;

  if (error || !order) {
    return (
      <div className="transfer-page">
        <div className="transfer-card">
          <h1>{TXT.title}</h1>
          <p className="transfer-error">{error || 'Order not found'}</p>
          <Link to="/" className="transfer-back-link">{TXT.back}</Link>
        </div>
      </div>
    );
  }

  const isPaid = order.payment_status === 'paid';
  const method = order.payment_method;
  const awaitingQuote = !order.shipping_quoted || order.shipping_quote_required;
  const b = order.bank_info || {};
  const a = order.shipping_address || {};
  const hasBank = !!(b.bank_name || b.bank_account_holder || b.bank_account_number || b.bank_clabe || b.bank_swift);

  const row = (key, label, value) => {
    if (!value) return null;
    return (
      <div className="bank-row" key={key}>
        <span className="bank-label">{label}</span>
        <span className="bank-value" translate="no">{value}</span>
        <button className="copy-btn" onClick={() => copy(key, value)} aria-label="copy">
          {copiedKey === key ? <Check size={16} /> : <Copy size={16} />}
        </button>
      </div>
    );
  };

  return (
    <div className="transfer-page">
      <Link to="/" className="transfer-logo">C. LLAGUNO</Link>

      <div className="transfer-card">
        <span className={`transfer-status ${isPaid ? 'paid' : awaitingQuote ? 'quoting' : 'pending'}`}>
          {isPaid ? TXT.paid : (awaitingQuote ? TXT.quoting : TXT.pending)}
        </span>
        <h1>{TXT.title}</h1>

        <div className="transfer-totals">
          <div>
            <span className="totals-label">{TXT.reference}</span>
            <span className="totals-value mono" translate="no">{order.reference}</span>
          </div>
          <div>
            <span className="totals-label">{awaitingQuote ? TXT.totalPartial : TXT.total}</span>
            <span className="totals-value bold" translate="no">{formatUSD(order.amount)} USD</span>
          </div>
        </div>

        {awaitingQuote && (
          <div className="quoting-block">
            <h2 className="transfer-section-title">{TXT.quoteTitle}</h2>
            <p className="paypal-desc">{TXT.quoteBody}</p>
          </div>
        )}

        {/* Payment-method-specific block — only when quote is settled */}
        {!awaitingQuote && method === 'paypal' && order.paypal_pay_url && (
          <div className="paypal-block">
            <h2 className="transfer-section-title">{TXT.paypalTitle}</h2>
            <p className="paypal-desc">{TXT.paypalDesc}</p>
            <a
              href={order.paypal_pay_url}
              target="_blank"
              rel="noopener noreferrer"
              className="paypal-cta"
              data-testid="paypal-pay-link"
            >
              <PayPalIcon size={20} />
              <span><strong>{TXT.paypalCta}</strong> · {formatUSD(order.amount)} USD <ExternalLink size={14} style={{ marginLeft: 6, opacity: 0.7 }} /></span>
            </a>
            <p className="transfer-instructions">{TXT.instructionsPaypal}</p>
          </div>
        )}

        {!awaitingQuote && method === 'transfer' && (
          <div className="transfer-section">
            <h2 className="transfer-section-title">{TXT.bank}</h2>
            {hasBank ? (
              <div className="bank-grid">
                {row('bank_name', TXT.bankName, b.bank_name)}
                {row('bank_account_holder', TXT.holder, b.bank_account_holder)}
                {row('bank_account_number', TXT.account, b.bank_account_number)}
                {row('bank_clabe', TXT.clabe, b.bank_clabe)}
                {row('bank_swift', TXT.swift, b.bank_swift)}
                {row('bank_reference_note', TXT.referenceNote, b.bank_reference_note)}
                {row('reference', TXT.reference, order.reference)}
              </div>
            ) : (
              <p className="bank-soon">{TXT.bankSoon}</p>
            )}
            <p className="transfer-instructions">{TXT.instructionsTransfer}</p>
          </div>
        )}

        {/* Shipping address */}
        {a && a.line1 && (
          <div className="transfer-section">
            <h2 className="transfer-section-title">{TXT.address}</h2>
            <p className="ship-address-text">
              {a.name}<br />
              {a.line1}{a.line2 ? `, ${a.line2}` : ''}<br />
              {a.city}{a.state ? `, ${a.state}` : ''} {a.postal_code}<br />
              {countryName(a.country, lang)}
            </p>
          </div>
        )}

        {/* Summary */}
        <div className="transfer-section">
          <h2 className="transfer-section-title">{TXT.summary}</h2>
          <ul className="order-items">
            {(order.items || []).map((it, idx) => (
              <li key={idx}>
                <span translate="no">
                  {it.title}{it.frame ? ` — ${frameLabel(it.frame, lang)}` : ''}
                </span>
                <span translate="no">{formatUSD(it.price)} USD</span>
              </li>
            ))}
            <li className="order-items-subtotal">
              <span>{TXT.subtotal}</span>
              <span translate="no">{formatUSD(order.subtotal || 0)} USD</span>
            </li>
            <li className="order-items-subtotal">
              <span>{TXT.shipping}</span>
              <span translate="no">
                {awaitingQuote
                  ? TXT.quoting
                  : (order.shipping_amount > 0 ? `${formatUSD(order.shipping_amount)} USD` : TXT.free)}
              </span>
            </li>
            <li className="order-items-grand">
              <span>{awaitingQuote ? TXT.totalPartial : TXT.total}</span>
              <span translate="no">{formatUSD(order.amount)} USD</span>
            </li>
          </ul>
        </div>

        <a href={`mailto:${TXT.contactEmail}?subject=${encodeURIComponent('Order ' + order.reference)}`} className="contact-link">
          <Mail size={16} /> {TXT.contact}
        </a>
        <Link to="/" className="transfer-back-link">{TXT.back}</Link>
      </div>
    </div>
  );
};

export default OrderDetails;
